'use client';

import Link from 'next/link';
import { useEffect, useRef } from 'react';
import { ArrowRight, Eraser, Image as ImageIcon, Network, Sparkles } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { getSmartFlowContextPayload, getSmartFlowNode, getSmartFlowRelated } from '@/config/smart-flow';
import { formatFlowBytes, smartFlowStore, useSmartFlow } from '@/lib/smart-flow';
import { EphemeralJobSession } from '@/lib/intelligence/job-session';

export function SmartFlowActions({ sourceToolSlug }: { sourceToolSlug?: string }) {
  const router = useRouter();
  const state = useSmartFlow();
  const asset = smartFlowStore.getActiveAsset();
  const context = smartFlowStore.getActiveContext();
  const source = sourceToolSlug ? getSmartFlowNode(sourceToolSlug) : undefined;
  const actions = sourceToolSlug ? getSmartFlowRelated(sourceToolSlug) : [];
  const canAssetHandoff = Boolean(source && asset && asset.producerToolSlug === source.slug && source.mode === 'asset-handoff');
  const canContextHandoff = Boolean(source && context && context.producerToolSlug === source.slug && source.mode === 'context-handoff');
  const canHandoff = canAssetHandoff || canContextHandoff;
  const sessionRef = useRef<EphemeralJobSession | null>(null);
  if (!sessionRef.current) sessionRef.current = new EphemeralJobSession();

  useEffect(() => () => sessionRef.current?.dispose(), []);
  const observeSelection = (action: { slug: string }, position: 1 | 2 | 3) => {
    if (!source) return;
    const decisionId = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          const v = c === 'x' ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        });
    sessionRef.current?.decision({
      decisionId, fromJobId: source.slug, contextKey: 'static-smart-flow',
      shown: actions.slice(0, 3).map((item, index) => ({ toJobId: item.slug, position: (index + 1) as 1 | 2 | 3, propensity: 1, explored: false })),
      selectedJobId: action.slug, selectedPosition: position, selectedCompleted: false, handoff: canHandoff ? 'started' : 'none',
    });
  };

  if (!source || actions.length === 0) return null;

  return (
    <section className="mt-5 rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 to-white p-4 sm:p-5" aria-label="Bước xử lý tiếp theo">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white"><Sparkles className="h-5 w-5" /></span>
          <div>
            <h2 className="font-bold text-slate-900">{canAssetHandoff ? 'Tiếp tục với ảnh này' : canContextHandoff ? 'Tiếp tục với kết quả này' : `Công cụ liên quan: ${source.label}`}</h2>
            {canAssetHandoff ? <p className="mt-0.5 text-xs leading-5 text-slate-600"><ImageIcon className="mr-1 inline h-3.5 w-3.5" />{asset?.name} · {formatFlowBytes(asset?.size || 0)}. Ảnh chỉ được giữ trong phiên hiện tại trên thiết bị của bạn.</p> : canContextHandoff ? <p className="mt-0.5 text-xs leading-5 text-slate-600">Các số liệu kết quả phù hợp sẽ được điền sẵn ở bước tiếp theo.</p> : <p className="mt-0.5 text-xs leading-5 text-slate-600"><Network className="mr-1 inline h-3.5 w-3.5" />Các bước được chọn theo nhóm công việc; không phải mọi tiện ích đều cần liên kết luồng.</p>}
          </div>
        </div>
        {canHandoff && <button type="button" onClick={() => smartFlowStore.clear()} className="inline-flex items-center justify-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-white hover:text-rose-700">
          <Eraser className="h-3.5 w-3.5" /> Kết thúc phiên
        </button>}
      </div>
      <div className="mt-4 grid gap-2 sm:grid-cols-3">
        {actions.map((action) => canHandoff ? (
          <button key={action.slug} type="button" onClick={() => { observeSelection(action, (actions.indexOf(action) + 1) as 1 | 2 | 3); if (canAssetHandoff) smartFlowStore.continueWith(action.slug); if (canContextHandoff && context) smartFlowStore.continueWithContext(action.slug, getSmartFlowContextPayload(source.slug, action.slug, context.values), source.slug, source.label); router.push(`/${action.categorySlug}/${action.slug}`); }} className="group flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-3 text-left text-sm font-semibold text-slate-800 transition hover:border-blue-400 hover:text-blue-700">
            {action.label}<ArrowRight className="h-4 w-4 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-blue-600" />
          </button>
        ) : (
          <Link key={action.slug} onClick={() => observeSelection(action, (actions.indexOf(action) + 1) as 1 | 2 | 3)} href={`/${action.categorySlug}/${action.slug}`} className="group flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-3 text-left text-sm font-semibold text-slate-800 transition hover:border-blue-400 hover:text-blue-700">
            {action.label}<ArrowRight className="h-4 w-4 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-blue-600" />
          </Link>
        ))}
      </div>
      {state.lastWarning && <p className="mt-3 text-xs font-medium text-amber-700">{state.lastWarning}</p>}
    </section>
  );
}
